/* eslint-disable @typescript-eslint/ban-types */
import { v4 } from 'uuid';
import { useEffect, useState } from 'react';

import { Async, PickUnion } from '@client/new/helpers/types';
import {
  FeatureCode,
  GetOfferQuery,
  useGetOfferQuery,
  usePurchasePageConfigQuery,
  useUpdatePurchasePageConfigMutation,
} from 'generated/graphql';

import { toDomainSections, toServerSections } from '../mappers';
import { useAsyncQueryRaw } from '@client/new/helpers/gql';
import {
  LandingPageModel,
  SectionName,
  sectionsConstructorMap,
} from '../domain';
import { ServerOfferSection } from '../sections/offer';

export const mockedOfferSection: ServerOfferSection = {
  type: 'Offer',
  id: 'fc3e88f2-c22c-4874-b114-f2593ec6952b',
  bgColor: 'primaryColor',
  enabled: true,
  headlineText: 'Join our Program',
  programIds: [],
  extrasIds: [],
  communityFeatures: [FeatureCode.Community],
};

export type OfferLandingPageSectionName = PickUnion<
  SectionName,
  'Introduction' | 'Offer'
>;

export type OfferLandingPageModel =
  LandingPageModel<OfferLandingPageSectionName>;

export const useOfferLandingPageModel = (p: {
  programId: number;
  offerId: number;
  creatorId: number;
}): Async<OfferLandingPageModel> => {
  const purchasePageConfigQuery = usePurchasePageConfigQuery({
    variables: { programId: p.programId },
  });

  const offerQuery = useGetOfferQuery({
    variables: {
      creatorIdID: p.creatorId + '',
      creatorIdInt: p.creatorId,
      bundleId: p.offerId,
    },
  });

  const { data: asyncSectionsData } = useAsyncQueryRaw({
    query: offerQuery,
    mapper: (offer) => toSectionsData({ offer }),
  });

  const data: ServerPurchasePageConfigQueryResult =
    purchasePageConfigQuery?.data?.purchasePageConfig;

  const [rawSections, setRawSections] = useState<Array<any>>([]);

  useEffect(() => {
    if (!data?.config?.sections) return;
    setRawSections(
      [mockedOfferSection, ...(data?.config?.sections || [])]
        .map((s) => ({
          ...s,
          id: s.id || v4(),
        }))
        .filter((s) => s.type === 'Introduction' || s.type === 'Offer'),
    );
  }, [data?.config?.sections.length]);

  const [updateMutation] = useUpdatePurchasePageConfigMutation();

  if (asyncSectionsData.type !== 'fetched') return asyncSectionsData;
  if (!data)
    return { type: 'fetch-error', value: 'No purchasePageConfig data' };

  const save: OfferLandingPageModel['save'] = async (sections) =>
    updateMutation({
      variables: {
        data: {
          id: data?.config?.id || 0,
          programId: p.programId,
          config: { sections: toServerSections(sections) },
        },
      },
    });

  return {
    type: 'fetched',
    value: {
      sections: toDomainSections(rawSections),
      sectionData: asyncSectionsData.value,
      save,
    },
  };
};

type ServerPurchasePageConfigQueryResult = {
  config?: { sections: Array<any>; id?: number };
  id: number;
};

export const toSectionsData = (raw: {
  offer: GetOfferQuery;
}): OfferLandingPageModel['sectionData'] => ({
  Offer: sectionsConstructorMap['Offer'].toData(raw.offer),
  base: { bgColor: { options: [] } },
});
